<template>
  <div class="step-container">
    <!-- Header Section -->
    <StepHeader :currentStep="currentStep" :steps="steps" />
    <!-- Content Section -->
    <div class="content-section">
      <div class="wizard-survey" v-if="masterSurvey && masterSurvey.Survey && masterSurvey.Survey.pages">
        <!-- Locale Selector -->
        <div class="wizard-survey-panel-locale-header">
          <div style="width: 50%;"><b>{{ lang.W00081 }}</b></div>
          <div style="width: 50%;">
            <b>{{ lang.W00082 }}</b>
            <select 
              class="wizard-survey-locale-code-select"
              v-model="selectedLocaleCode"
            >
              <option :value="null" disabled hidden>{{ lang.W00083 }}</option>
              <option
                v-for="value in surveyLocales"
                :key="value.LocaleCode"
                :value="value.LocaleCode"
              >
                {{ value.LocaleName }}
              </option>
            </select>
          </div>
        </div>
        <!-- Header -->
        <div class="wizard-survey-panel-header">
          <div class="wizard-survey-panel-header-btn" @click="moveHeader('l')">
            <i class="material-icons">chevron_left</i>
          </div>
          <div class="wizard-survey-panel-header-elements-container" @wheel.prevent="scrollHeader($event)">
            <div class="wizard-survey-panel-header-elements" ref="wizard-survey-panel-header-elements">
              <template v-for="page in masterSurvey.Survey.pages">
                <div 
                  v-for="cluster in page.elements" 
                  :key="`pagination-${cluster.guid.toUpperCase()}`" 
                  class="wizard-survey-panel-header-element"
                  :class="{ active: activeCluster == cluster }"
                  @click="activeCluster = cluster"
                >
                  <span>{{ cluster.title }}</span>
                  <!-- scenarioQuestionSettings -->
                  <span v-if="scenarioQuestionSettings_Visible[cluster.guid.toUpperCase()] || scenarioQuestionSettings_NotVisible[cluster.guid.toUpperCase()]" class="scenario-question-settings-container">
                    <i class="material-icons">visibility</i>                    
                  </span>
                </div>
              </template>
            </div>
          </div>
          <div class="wizard-survey-panel-header-btn" @click="moveHeader('r')">
            <i class="material-icons">chevron_right</i>
          </div>
        </div>
        <!-- SubHeader -->
        <div 
          class="wizard-survey-panel-sub-header" 
          style="justify-content: flex-start; padding-left: 5px; min-height: 5px;"
        >
          <div class="wizard-survey-row" v-if="activeCluster && (scenarioQuestionSettings_Visible[activeCluster.guid.toUpperCase()] || scenarioQuestionSettings_NotVisible[activeCluster.guid.toUpperCase()])">
            <div v-if="scenarioQuestionSettings_Visible[activeCluster.guid.toUpperCase()] && scenarioQuestionSettings_Visible[activeCluster.guid.toUpperCase()].Scenarios">
              <span>{{ lang.W00087 }}</span>
              <span 
                v-if="
                  scenarioQuestionSettings_Visible[activeCluster.guid.toUpperCase()].Scenarios && 
                  scenarioQuestionSettings_Visible[activeCluster.guid.toUpperCase()].Scenarios.length == 0 &&
                  scenarioQuestionSettings_Visible[activeCluster.guid.toUpperCase()].ScenariosNotInConfig && 
                  scenarioQuestionSettings_Visible[activeCluster.guid.toUpperCase()].ScenariosNotInConfig.length != 0
                "
              >{{ lang.W00098 }}&nbsp;{{ lang.W00099 }}</span>
              <span v-else v-for="(scenario, index) in scenarioQuestionSettings_Visible[activeCluster.guid.toUpperCase()].Scenarios">
                <span v-if="index">,&nbsp;</span>
                {{ scenario }}
              </span>
            </div>
            <div v-if="scenarioQuestionSettings_NotVisible[activeCluster.guid.toUpperCase()] && scenarioQuestionSettings_NotVisible[activeCluster.guid.toUpperCase()].Scenarios">
              <span>{{ lang.W00088 }}</span>
              <span 
                v-if="
                  scenarioQuestionSettings_NotVisible[activeCluster.guid.toUpperCase()].Scenarios && 
                  scenarioQuestionSettings_NotVisible[activeCluster.guid.toUpperCase()].Scenarios.length == 0 &&
                  scenarioQuestionSettings_NotVisible[activeCluster.guid.toUpperCase()].ScenariosNotInConfig && 
                  scenarioQuestionSettings_NotVisible[activeCluster.guid.toUpperCase()].ScenariosNotInConfig.length != 0
                "
              >{{ lang.W00098 }}&nbsp;{{ lang.W00100 }}</span>
              <span v-else v-for="(scenario, index) in scenarioQuestionSettings_NotVisible[activeCluster.guid.toUpperCase()].Scenarios">
                <span v-if="index">,&nbsp;</span>
                {{ scenario }}
              </span>
            </div>            
          </div>
        </div>

        <!-- pages -->
        <div v-for="page in masterSurvey.Survey.pages" :key="page.guid">
          <!-- panel (cluster) -->
          <div v-for="cluster in page.elements" v-show="activeCluster.guid.toUpperCase() == cluster.guid.toUpperCase()">
            <!-- Cluster Title -->
            <div class="wizard-survey-panel-sub-header" :key="cluster.guid">
              <div class="wizard-survey-row-half">
                <div class="wizard-survey-panel-title">
                  <b>
                    <span 
                      class="wizard-survey-contenteditable text-bold"
                      ref="wizard-survey-contenteditable" 
                      :contenteditable="false" 
                      :data-questionguid="cluster.guid.toUpperCase()"
                      :data-key="'Title'"
                      :data-fallbacktext="cluster.title"
                    ></span>
                  </b>
                </div>
              </div>
              <div class="wizard-survey-row-half">
                <!-- Translation Panel Title -->
                <input
                  type="text"
                  class="wizard-survey-translation-input"
                  ref="wizard-survey-translation-input"
                  :data-translationguid="cluster.guid.toUpperCase()"
                  :data-translationkey="'title'"
                  :data-category="'question'"
                  :disabled="true"
                ></input>
              </div>
            </div>
            <!-- Cluster Description -->
            <div class="wizard-survey-panel-sub-header" style="padding-bottom: 5px; border-bottom: 2px solid #e0e0e0;">
              <div class="wizard-survey-row-half">
                <div style="width: 5px"></div>
                <span 
                  class="wizard-survey-contenteditable"
                  :class="{
                    'placeholder-active': !cluster.description && !(questionsByGuid[cluster.guid.toUpperCase()] && questionsByGuid[cluster.guid.toUpperCase()].Description)
                  }"
                  ref="wizard-survey-contenteditable"
                  :contenteditable="false"
                  :data-questionguid="cluster.guid.toUpperCase()"
                  :data-key="'Description'"
                  :data-fallbacktext="cluster.description ? cluster.description : lang.W00085"
                  :data-placeholder="(cluster.description || (questionsByGuid[cluster.guid.toUpperCase()] && questionsByGuid[cluster.guid.toUpperCase()].Description)) ? false : true"
                ></span>
              </div>
              <div class="wizard-survey-row-half">
                <!-- Translation description -->
                <input
                  type="text"
                  class="wizard-survey-translation-input"
                  ref="wizard-survey-translation-input"
                  :data-translationguid="cluster.guid.toUpperCase()"
                  :data-translationkey="'description'"
                  :data-category="'question'"
                  :disabled="true"
                ></input>
              </div>
            </div>

            <template v-for="element1 in cluster.elements">
              <!-- panel (question group) -->
              <div v-if="element1.type == 'panel'" class="wizard-survey-panel">
                <!-- Panel Title -->
                <div class="wizard-survey-row">
                  <div class="wizard-survey-row-half">
                    <div class="wizard-survey-panel-title">
                      <!-- <b>{{ element1.title }}</b> -->
                      <b>
                        <span 
                          class="wizard-survey-contenteditable text-bold"
                          ref="wizard-survey-contenteditable" 
                          :contenteditable="!readonly" 
                          @input="setConfigurationQuestion(element1.guid.toUpperCase(), 'Title', $event.target.textContent, $event.target)"
                          :data-questionguid="element1.guid.toUpperCase()"
                          :data-key="'Title'"
                          :data-fallbacktext="element1.title"
                        ></span>
                      </b>
                      <!-- {{ element1.guid }} -->
                      <!-- scenarioQuestionSettings -->
                      <span v-if="scenarioQuestionSettings_Visible[element1.guid.toUpperCase()] || scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()]" class="scenario-question-settings-container">
                        <i class="material-icons">visibility</i>                    
                        <div class="scenario-question-settings-popup">
                          <div v-if="scenarioQuestionSettings_Visible[element1.guid.toUpperCase()] && scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].Scenarios">
                            <span>{{ lang.W00087 }}</span>
                            <span 
                              v-if="
                                scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].Scenarios && 
                                scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].Scenarios.length == 0 &&
                                scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].ScenariosNotInConfig && 
                                scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].ScenariosNotInConfig.length != 0
                              "
                            >
                              {{ lang.W00098 }}&nbsp;{{ lang.W00099 }}
                            </span>
                            <span v-else v-for="(scenario, index) in scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].Scenarios">
                              <span v-if="index">,&nbsp;</span>
                              {{ scenario }}
                            </span>
                          </div>
                          <div v-if="scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()] && scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].Scenarios">
                            <span>{{ lang.W00088 }}</span>
                            <span 
                              v-if="
                                scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].Scenarios && 
                                scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].Scenarios.length == 0 &&
                                scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].ScenariosNotInConfig && 
                                scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].ScenariosNotInConfig.length != 0
                              "
                            >
                              {{ lang.W00098 }}&nbsp;{{ lang.W00100 }}
                            </span>
                            <span v-else v-for="(scenario, index) in scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].Scenarios">
                              <span v-if="index">,&nbsp;</span>
                              {{ scenario }}
                            </span>
                          </div>
                        </div>
                      </span>
                    </div>
                  </div>
                  <div class="wizard-survey-row-half">
                    <!-- Translation Panel Title -->
                    <input
                      type="text"
                      class="wizard-survey-translation-input"
                      ref="wizard-survey-translation-input"
                      :data-translationguid="element1.guid.toUpperCase()"
                      :data-translationkey="'title'"
                      :data-category="'question'"
                      @input="setConfigurationQuestionTranslation(element1.guid.toUpperCase(), 'TranslationTitle', $event.target.value, $event.target)"
                      :disabled="readonly || !selectedLocaleCode"
                    ></input>
                  </div>
                </div>
                <!-- Panel Description -->
                <div class="wizard-survey-row">
                  <div class="wizard-survey-row-half">
                    <div style="width: 5px"></div>
                    <span 
                      class="wizard-survey-contenteditable"
                      :class="{
                        'placeholder-active': !element1.description && !(questionsByGuid[element1.guid.toUpperCase()] && questionsByGuid[element1.guid.toUpperCase()].Description)
                      }"
                      ref="wizard-survey-contenteditable"
                      :contenteditable="!readonly"
                      @click="removePlaceholder($event.target)"
                      @input="setConfigurationQuestion(element1.guid.toUpperCase(), 'Description', $event.target.textContent, $event.target)"
                      :data-questionguid="element1.guid.toUpperCase()"
                      :data-key="'Description'"
                      :data-fallbacktext="element1.description ? element1.description : lang.W00085"
                      :data-placeholder="(element1.description || (questionsByGuid[element1.guid.toUpperCase()] && questionsByGuid[element1.guid.toUpperCase()].Description)) ? false : true"
                    ></span>
                  </div>
                  <div class="wizard-survey-row-half">
                    <!-- Translation description -->
                    <input
                      type="text"
                      class="wizard-survey-translation-input"
                      ref="wizard-survey-translation-input"
                      :data-translationguid="element1.guid.toUpperCase()"
                      :data-translationkey="'description'"
                      :data-category="'question'"
                      @input="setConfigurationQuestionTranslation(element1.guid.toUpperCase(), 'TranslationDescription', $event.target.value, $event.target)"
                      :disabled="readonly || !selectedLocaleCode"
                    ></input>
                  </div>
                </div>

                <!-- questions -->
                <div v-for="element2 in element1.elements" class="wizard-survey-question">
                  <span v-if="debug">{{ element2.guid.toUpperCase() }}</span>
                  <div class="wizard-survey-row">
                    <div class="wizard-survey-row-half">
                      <input 
                        class="wizard-survey-input-isSelected"
                        :key="`wizard-survey-input-${element2.guid.toUpperCase()}-IsSelected`"
                        type="checkbox" 
                        :checked="(questionsByGuid[element2.guid.toUpperCase()]) ? questionsByGuid[element2.guid.toUpperCase()].IsSelected : true" 
                        @change="setConfigurationQuestion(element2.guid.toUpperCase(), 'IsSelected', $event.target.checked, $event.target)"
                        :disabled="readonly"
                      >
                      <span class="wizard-survey-question-title">
                        <span class="text-bold">{{ element2.customQuestionNumber }}</span>
                        <span 
                          class="wizard-survey-contenteditable text-bold"
                          ref="wizard-survey-contenteditable" 
                          :contenteditable="!readonly" 
                          @input="setConfigurationQuestion(element2.guid.toUpperCase(), 'Title', $event.target.textContent, $event.target)"
                          :data-questionguid="element2.guid.toUpperCase()"
                          :data-key="'Title'"
                          :data-fallbacktext="element2.title"
                        ></span>
                        <span class="text-bold">{{ element2.isRequired ? '*' : '' }}</span>
                        <em>({{ element2.type }})</em>
                        <span v-show="false">{{ element2.guid.toUpperCase() }}</span>
                      </span>
                      
                      <!-- scenarioQuestionSettings -->
                      <span v-if="scenarioQuestionSettings_Visible[element2.guid.toUpperCase()] || scenarioQuestionSettings_NotVisible[element2.guid.toUpperCase()]" class="scenario-question-settings-container">
                        <i class="material-icons">visibility</i>                    
                        <div class="scenario-question-settings-popup">
                          <div v-if="scenarioQuestionSettings_Visible[element2.guid.toUpperCase()] && scenarioQuestionSettings_Visible[element2.guid.toUpperCase()].Scenarios">
                            <span>{{ lang.W00087 }}</span>
                            <span 
                              v-if="
                                scenarioQuestionSettings_Visible[element2.guid.toUpperCase()].Scenarios && 
                                scenarioQuestionSettings_Visible[element2.guid.toUpperCase()].Scenarios.length == 0 &&
                                scenarioQuestionSettings_Visible[element2.guid.toUpperCase()].ScenariosNotInConfig && 
                                scenarioQuestionSettings_Visible[element2.guid.toUpperCase()].ScenariosNotInConfig.length != 0
                              "
                            >{{ lang.W00098 }}&nbsp;{{ lang.W00099 }}</span>
                            <span v-else v-for="(scenario, index) in scenarioQuestionSettings_Visible[element2.guid.toUpperCase()].Scenarios">
                              <span v-if="index">,&nbsp;</span>
                              {{ scenario }}
                            </span>
                          </div>
                          <div v-if="scenarioQuestionSettings_NotVisible[element2.guid.toUpperCase()] && scenarioQuestionSettings_NotVisible[element2.guid.toUpperCase()].Scenarios">
                            <span>{{ lang.W00088 }}</span>
                            <span 
                              v-if="
                                scenarioQuestionSettings_NotVisible[element2.guid.toUpperCase()].Scenarios && 
                                scenarioQuestionSettings_NotVisible[element2.guid.toUpperCase()].Scenarios.length == 0 &&
                                scenarioQuestionSettings_NotVisible[element2.guid.toUpperCase()].ScenariosNotInConfig && 
                                scenarioQuestionSettings_NotVisible[element2.guid.toUpperCase()].ScenariosNotInConfig.length != 0
                              "
                            >{{ lang.W00098 }}&nbsp;{{ lang.W00100 }}</span>
                            <span v-else v-for="(scenario, index) in scenarioQuestionSettings_NotVisible[element2.guid.toUpperCase()].Scenarios">
                              <span v-if="index">,&nbsp;</span>
                              {{ scenario }}
                            </span>
                          </div>
                        </div>
                      </span>

                      <!-- Max points -->
                      <span 
                        class="span-max-points" 
                        v-if="element2.type == 'checkbox'"
                      >
                        {{ lang.W00084 }}
                        <span
                          class="wizard-survey-contenteditable" 
                          ref="wizard-survey-contenteditable" 
                          :contenteditable="!readonly" 
                          @input="setConfigurationQuestion(element2.guid.toUpperCase(), 'PointsMax', $event.target.textContent, $event.target, 'number')"
                          :data-questionguid="element2.guid.toUpperCase()"
                          :data-key="'PointsMax'"
                          :data-fallbacktext="element2.pointsMax ? element2.pointsMax : 0"
                        ></span>
                      </span>                  
                    </div>
                    <div class="wizard-survey-row-half">
                      <!-- Translation Title -->
                      <input
                        type="text"
                        class="wizard-survey-translation-input"
                        ref="wizard-survey-translation-input"
                        :data-translationguid="element2.guid.toUpperCase()"
                        :data-translationkey="'title'"
                        :data-category="'question'"
                        @input="setConfigurationQuestionTranslation(element2.guid.toUpperCase(), 'TranslationTitle', $event.target.value, $event.target)"
                        :disabled="readonly || !selectedLocaleCode"
                      ></input>
                    </div>
                  </div>
                  <!-- Description -->
                  <div class="wizard-survey-row">
                    <div class="wizard-survey-row-half">
                      <div class="wizard-survey-spacer"></div>
                      <span 
                        class="wizard-survey-contenteditable"
                        :class="{
                          'placeholder-active': !element2.description && !(questionsByGuid[element2.guid.toUpperCase()] && questionsByGuid[element2.guid.toUpperCase()].Description)
                        }"
                        ref="wizard-survey-contenteditable"
                        :contenteditable="!readonly"
                        @click="removePlaceholder($event.target)"
                        @input="setConfigurationQuestion(element2.guid.toUpperCase(), 'Description', $event.target.textContent, $event.target)"
                        :data-questionguid="element2.guid.toUpperCase()"
                        :data-key="'Description'"
                        :data-fallbacktext="element2.description ? element2.description : lang.W00085"
                        :data-placeholder="(element2.description || (questionsByGuid[element2.guid.toUpperCase()] && questionsByGuid[element2.guid.toUpperCase()].Description)) ? false : true"
                      ></span>
                    </div>
                    <div class="wizard-survey-row-half">
                      <!-- Translation description -->
                      <input
                        type="text"
                        class="wizard-survey-translation-input"
                        ref="wizard-survey-translation-input"
                        :data-translationguid="element2.guid.toUpperCase()"
                        :data-translationkey="'description'"
                        :data-category="'question'"
                        @input="setConfigurationQuestionTranslation(element2.guid.toUpperCase(), 'TranslationDescription', $event.target.value, $event.target)"
                        :disabled="readonly || !selectedLocaleCode"
                      ></input>
                    </div>
                  </div>
                  <!-- Choices -->
                  <div v-if="element2.choices" v-for="c in element2.choices" class="wizard-survey-row">
                    <div class="wizard-survey-row-half">
                      <div class="wizard-survey-spacer"></div>
                      <input v-if="element2.type == 'radiogroup'" type="radio" name="" id="" disabled style="cursor: default; margin: 0px;">
                      <input v-if="element2.type == 'checkbox'" type="checkbox" name="" id="" disabled style="cursor: default; margin: 0px;">
                      <div style="text-align: justify; max-width: 80%;">
                        <span
                          class="wizard-survey-contenteditable" 
                          ref="wizard-survey-contenteditable" 
                          :contenteditable="!readonly" 
                          @input="setConfigurationQuestionChoice(c.guid.toUpperCase(), 'Title', $event.target.textContent, $event.target)"
                          :data-choiceguid="c.guid.toUpperCase()"
                          :data-key="'Title'"
                          :data-fallbacktext="c.text"
                        ></span>                     
                      </div>

                      <!-- Points -->
                      <span v-if="element2.type == 'radiogroup'" class="wizard-survey-points-container">
                        (
                        <span
                          class="wizard-survey-contenteditable" 
                          ref="wizard-survey-contenteditable" 
                          :contenteditable="!readonly" 
                          @input="setConfigurationQuestionChoice(c.guid.toUpperCase(), 'Points', $event.target.textContent, $event.target, 'number')"
                          :data-choiceguid="c.guid.toUpperCase()"
                          :data-key="'Points'"
                          :data-fallbacktext="c.points ? c.points : 0"
                        ></span>
                        /
                        <span
                          class="wizard-survey-contenteditable text-center" 
                          ref="wizard-survey-contenteditable" 
                          :contenteditable="!readonly" 
                          @input="setConfigurationQuestionChoice(c.guid.toUpperCase(), 'PointsMax', $event.target.textContent, $event.target, 'number')"
                          :data-choiceguid="c.guid.toUpperCase()"
                          :data-key="'PointsMax'"
                          :data-fallbacktext="element2.pointsMax ? element2.pointsMax : 0"
                        ></span>
                        )
                      </span>
                      <span v-if="element2.type == 'checkbox'" class="wizard-survey-points-container">
                        (
                        <span
                          class="wizard-survey-contenteditable" 
                          ref="wizard-survey-contenteditable" 
                          :contenteditable="!readonly" 
                          @input="setConfigurationQuestionChoice(c.guid.toUpperCase(), 'Points', $event.target.textContent, $event.target, 'number')"
                          :data-choiceguid="c.guid.toUpperCase()"
                          :data-key="'Points'"
                          :data-fallbacktext="c.points ? c.points : 0"
                        ></span>
                        )
                      </span>
                    </div>
                    <div class="wizard-survey-row-half">
                      <!-- Translation choice text -->
                      <input
                        type="text"
                        class="wizard-survey-translation-input"
                        ref="wizard-survey-translation-input"
                        :data-translationguid="c.guid.toUpperCase()"
                        :data-translationkey="'text'"
                        :data-category="'choice'"
                        @input="setConfigurationQuestionChoiceTranslation(c.guid.toUpperCase(), 'Translation', $event.target.value, $event.target)"
                        :disabled="readonly || !selectedLocaleCode"
                      ></input>
                    </div>
                  </div>
                  <div class="wizard-survey-row">
                    <textarea 
                      rows="1" 
                      :placeholder="lang.W00086"
                      @change="setConfigurationQuestion(element2.guid.toUpperCase(), 'Comment', $event.target.value, $event.target)"
                      :disabled="readonly"
                    >{{ getComment(element2.guid.toUpperCase()) }}</textarea>
                  </div>
                </div>
              </div>

              <!-- questions -->
              <div v-else class="wizard-survey-question">
                <span v-if="debug">{{ element1.guid.toUpperCase() }}</span>
                <div class="wizard-survey-row">
                  <div class="wizard-survey-row-half">
                    <input 
                      class="wizard-survey-input-isSelected"
                      :key="`wizard-survey-input-${element1.guid.toUpperCase()}-IsSelected`"
                      type="checkbox" 
                      :checked="(questionsByGuid[element1.guid.toUpperCase()]) ? questionsByGuid[element1.guid.toUpperCase()].IsSelected : true" 
                      @change="setConfigurationQuestion(element1.guid.toUpperCase(), 'IsSelected', $event.target.checked, $event.target)"
                      :disabled="readonly"
                    >
                    <span class="wizard-survey-question-title">
                      <span class="text-bold">{{ element1.customQuestionNumber }}</span>
                      <span 
                        class="wizard-survey-contenteditable text-bold"
                        ref="wizard-survey-contenteditable" 
                        :contenteditable="!readonly" 
                        @input="setConfigurationQuestion(element1.guid.toUpperCase(), 'Title', $event.target.textContent, $event.target)"
                        :data-questionguid="element1.guid.toUpperCase()"
                        :data-key="'Title'"
                        :data-fallbacktext="element1.title"
                      ></span>
                      <span class="text-bold">{{ element1.isRequired ? '*' : '' }}</span>
                      <em>({{ element1.type }})</em>
                      <span v-show="false">{{ element1.guid.toUpperCase() }}</span>
                    </span>
                    
                    <!-- scenarioQuestionSettings -->
                    <span v-if="scenarioQuestionSettings_Visible[element1.guid.toUpperCase()] || scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()]" class="scenario-question-settings-container">
                      <i class="material-icons">visibility</i>                    
                      <div class="scenario-question-settings-popup">
                        <div v-if="scenarioQuestionSettings_Visible[element1.guid.toUpperCase()] && scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].Scenarios">
                          <span>{{ lang.W00087 }}</span>
                          <span 
                            v-if="
                              scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].Scenarios && 
                              scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].Scenarios.length == 0 &&
                              scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].ScenariosNotInConfig && 
                              scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].ScenariosNotInConfig.length != 0
                            "
                          >{{ lang.W00098 }}&nbsp;{{ lang.W00099 }}</span>
                          <span v-else v-for="(scenario, index) in scenarioQuestionSettings_Visible[element1.guid.toUpperCase()].Scenarios">
                            <span v-if="index">,&nbsp;</span>
                            {{ scenario }}
                          </span>
                        </div>
                        <div v-if="scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()] && scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].Scenarios">
                          <span>{{ lang.W00088 }}</span>
                          <span 
                            v-if="
                              scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].Scenarios && 
                              scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].Scenarios.length == 0 &&
                              scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].ScenariosNotInConfig && 
                              scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].ScenariosNotInConfig.length != 0
                            "
                          >{{ lang.W00098 }}&nbsp;{{ lang.W00100 }}</span>
                          <span v-else v-for="(scenario, index) in scenarioQuestionSettings_NotVisible[element1.guid.toUpperCase()].Scenarios">
                            <span v-if="index">,&nbsp;</span>
                            {{ scenario }}
                          </span>
                        </div>
                      </div>
                    </span>

                    <!-- Max points -->
                    <span 
                      class="span-max-points" 
                      v-if="element1.type == 'checkbox'"
                    >
                      {{ lang.W00084 }}
                      <span
                        class="wizard-survey-contenteditable" 
                        ref="wizard-survey-contenteditable" 
                        :contenteditable="!readonly" 
                        @input="setConfigurationQuestion(element1.guid.toUpperCase(), 'PointsMax', $event.target.textContent, $event.target, 'number')"
                        :data-questionguid="element1.guid.toUpperCase()"
                        :data-key="'PointsMax'"
                        :data-fallbacktext="element1.pointsMax ? element1.pointsMax : 0"
                      ></span>
                    </span>                  
                  </div>
                  <div class="wizard-survey-row-half">
                    <!-- Translation Title -->
                    <input
                      type="text"
                      class="wizard-survey-translation-input"
                      ref="wizard-survey-translation-input"
                      :data-translationguid="element1.guid.toUpperCase()"
                      :data-translationkey="'title'"
                      :data-category="'question'"
                      @input="setConfigurationQuestionTranslation(element1.guid.toUpperCase(), 'TranslationTitle', $event.target.value, $event.target)"
                      :disabled="readonly || !selectedLocaleCode"
                    ></input>
                  </div>
                </div>
                <!-- Description -->
                <div class="wizard-survey-row">
                  <div class="wizard-survey-row-half">
                    <div class="wizard-survey-spacer"></div>
                    <span 
                      class="wizard-survey-contenteditable"
                      :class="{
                        'placeholder-active': !element1.description && !(questionsByGuid[element1.guid.toUpperCase()] && questionsByGuid[element1.guid.toUpperCase()].Description)
                      }"
                      ref="wizard-survey-contenteditable"
                      :contenteditable="!readonly"
                      @click="removePlaceholder($event.target)"
                      @input="setConfigurationQuestion(element1.guid.toUpperCase(), 'Description', $event.target.textContent, $event.target)"
                      :data-questionguid="element1.guid.toUpperCase()"
                      :data-key="'Description'"
                      :data-fallbacktext="element1.description ? element1.description : lang.W00085"
                      :data-placeholder="(element1.description || (questionsByGuid[element1.guid.toUpperCase()] && questionsByGuid[element1.guid.toUpperCase()].Description)) ? false : true"
                    ></span>
                  </div>
                  <div class="wizard-survey-row-half">
                    <!-- Translation description -->
                    <input
                      type="text"
                      class="wizard-survey-translation-input"
                      ref="wizard-survey-translation-input"
                      :data-translationguid="element1.guid.toUpperCase()"
                      :data-translationkey="'description'"
                      :data-category="'question'"
                      @input="setConfigurationQuestionTranslation(element1.guid.toUpperCase(), 'TranslationDescription', $event.target.value, $event.target)"
                      :disabled="readonly || !selectedLocaleCode"
                    ></input>
                  </div>
                </div>
                <!-- Choices -->
                <div v-if="element1.choices" v-for="c in element1.choices" class="wizard-survey-row">
                  <div class="wizard-survey-row-half">
                    <div class="wizard-survey-spacer"></div>
                    <input v-if="element1.type == 'radiogroup'" type="radio" name="" id="" disabled style="cursor: default; margin: 0px;">
                    <input v-if="element1.type == 'checkbox'" type="checkbox" name="" id="" disabled style="cursor: default; margin: 0px;">
                    <div style="text-align: justify; max-width: 80%;">
                      <span
                        class="wizard-survey-contenteditable" 
                        ref="wizard-survey-contenteditable" 
                        :contenteditable="!readonly" 
                        @input="setConfigurationQuestionChoice(c.guid.toUpperCase(), 'Title', $event.target.textContent, $event.target)"
                        :data-choiceguid="c.guid.toUpperCase()"
                        :data-key="'Title'"
                        :data-fallbacktext="c.text"
                      ></span>                     
                    </div>

                    <!-- Points -->
                    <span v-if="element1.type == 'radiogroup'" class="wizard-survey-points-container">
                      (
                      <span
                        class="wizard-survey-contenteditable" 
                        ref="wizard-survey-contenteditable" 
                        :contenteditable="!readonly" 
                        @input="setConfigurationQuestionChoice(c.guid.toUpperCase(), 'Points', $event.target.textContent, $event.target, 'number')"
                        :data-choiceguid="c.guid.toUpperCase()"
                        :data-key="'Points'"
                        :data-fallbacktext="c.points ? c.points : 0"
                      ></span>
                      /
                      <span
                        class="wizard-survey-contenteditable text-center" 
                        ref="wizard-survey-contenteditable" 
                        :contenteditable="!readonly" 
                        @input="setConfigurationQuestionChoice(c.guid.toUpperCase(), 'PointsMax', $event.target.textContent, $event.target, 'number')"
                        :data-choiceguid="c.guid.toUpperCase()"
                        :data-key="'PointsMax'"
                        :data-fallbacktext="element1.pointsMax ? element1.pointsMax : 0"
                      ></span>
                      )
                    </span>
                    <span v-if="element1.type == 'checkbox'" class="wizard-survey-points-container">
                      (
                      <span
                        class="wizard-survey-contenteditable" 
                        ref="wizard-survey-contenteditable" 
                        :contenteditable="!readonly" 
                        @input="setConfigurationQuestionChoice(c.guid.toUpperCase(), 'Points', $event.target.textContent, $event.target, 'number')"
                        :data-choiceguid="c.guid.toUpperCase()"
                        :data-key="'Points'"
                        :data-fallbacktext="c.points ? c.points : 0"
                      ></span>
                      )
                    </span>
                  </div>
                  <div class="wizard-survey-row-half">
                    <!-- Translation choice text -->
                    <input
                      type="text"
                      class="wizard-survey-translation-input"
                      ref="wizard-survey-translation-input"
                      :data-translationguid="c.guid.toUpperCase()"
                      :data-translationkey="'text'"
                      :data-category="'choice'"
                      @input="setConfigurationQuestionChoiceTranslation(c.guid.toUpperCase(), 'Translation', $event.target.value, $event.target)"
                      :disabled="readonly || !selectedLocaleCode"
                    ></input>
                  </div>
                </div>
                <div class="wizard-survey-row">
                  <textarea 
                    rows="1" 
                    :placeholder="lang.W00086"
                    @change="setConfigurationQuestion(element1.guid.toUpperCase(), 'Comment', $event.target.value, $event.target)"
                    :disabled="readonly"
                  >{{ getComment(element1.guid.toUpperCase()) }}</textarea>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "../../../styles/audi-steps.css";
import StepHeader from './StepHeader.vue';
import wizardService from "../../../services/WizardService";
import * as SurveyVue from "survey-vue";
import { readonly } from "vue";

export default {
  name: 'Step5',
  components: {
    StepHeader
  },
  props: {
    formData: { type: Object, required: true },
    currentStep: { type: Number, default: 0 },
    steps: { type: Array, required: true },
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      debug: false,
      localFormData: structuredClone(this.formData),
      masterSurvey: null,
      survey: null,
      clusterIndex: 0,
      activeCluster: null,
      translateX: 0,
      surveyLocales: null,
      selectedLocaleCode: null,
      translations: null,

      lang: {
        W00081: "Default",
        W00082: "Translation",
        W00083: "Select language…",
        W00084: "Max points:",
        W00085: "Description…",
        W00086: "Note…",
        W00087: "Only visible for:",
        W00088: "Not visible for:",
        W00098: "none of the chosen scenarios",
        W00099: "(=not visible)",
        W00100: "(=visible)",
      },
    }
  },
  computed: {
    outline() {
      if (!this.survey || !this.survey.pages) return [];

      const walkElements = (elements, pageName) => {
        return (elements || []).map((el, idx) => {
          const type = typeof el.getType === "function" ? el.getType() : el.type;
          const title = el.title || el.name || "(untitled)";

          // Panels 
          const children = el.elements || [];
          const questions = this.flattenQuestions(children, pageName, el.name || `panel_${idx}`);
          return {
            type: type,
            key: `${pageName}:panel:${el.name || idx}`,
            title,
            questions,
            guid: el.guid.toUpperCase()
          };
        });
      };

      return this.survey.pages.map((page) => {
        const pageTitle = page.title || page.name || "(untitled page)";
        return {
          key: `page:${page.name}`,
          title: pageTitle,
          items: walkElements(page.elements, page.name)
        };
      });
    },
    questionsByGuid() {
      const map = Object.create(null);
      const qs = this.localFormData.questions || [];
      for (const q of qs) {
        map[q.QuestionGuid] = q;
      }
      return map;
    },
    choicesByGuid() {
      const map = Object.create(null);
      const qc = this.localFormData.questionChoices || [];
      for (const q of qc) {
        map[q.ChoiceGuid] = q;
      }
      return map;
    },
    scenarioQuestionSettings_Visible() {
      if(!this.masterSurvey || !this.masterSurvey.ScenarioQuestionSettings) return [];
      const map = Object.create(null);
      const sqs = this.masterSurvey.ScenarioQuestionSettings || [];

      for (const s of sqs) {
        if(s.Visible) {
          let scenarioInConfig = true;
          if(!this.configurationScenarios[s.ScenarioId]) scenarioInConfig = false;

          if(map[s.ElementGuid] && !map[s.ElementGuid].Scenarios.includes(s.ScenarioName)) {
            if(scenarioInConfig) map[s.ElementGuid].Scenarios.push(s.ScenarioName);
            else if(!scenarioInConfig) map[s.ElementGuid].ScenariosNotInConfig.push(s.ScenarioName);
          } else {
            if(scenarioInConfig) map[s.ElementGuid] = { Scenarios: [s.ScenarioName], ScenariosNotInConfig: [] };
            else map[s.ElementGuid] = { Scenarios: [], ScenariosNotInConfig: [s.ScenarioName] };
          }
        }
      }
      return map;
    },
    scenarioQuestionSettings_NotVisible() {
      if(!this.masterSurvey || !this.masterSurvey.ScenarioQuestionSettings) return [];
      const map = Object.create(null);
      const sqs = this.masterSurvey.ScenarioQuestionSettings || [];

      for (const s of sqs) {
        if(!s.Visible) {
          let scenarioInConfig = true;
          if(!this.configurationScenarios[s.ScenarioId]) scenarioInConfig = false;

          if(map[s.ElementGuid] && !map[s.ElementGuid].Scenarios.includes(s.ScenarioName)) {
            if(scenarioInConfig) map[s.ElementGuid].Scenarios.push(s.ScenarioName);
            else if(!scenarioInConfig) map[s.ElementGuid].ScenariosNotInConfig.push(s.ScenarioName);
          } else {
            if(scenarioInConfig) map[s.ElementGuid] = { Scenarios: [s.ScenarioName], ScenariosNotInConfig: [] };
            else map[s.ElementGuid] = { Scenarios: [], ScenariosNotInConfig: [s.ScenarioName] };
          }
        }
      }
      return map;
    },
    configurationScenarios() {
      const map = Object.create(null);
      const sc = this.localFormData.scenarios || [];
      for (const s of sc) {
        map[s.ScenarioId] = s.ScenarioName;
      }
      return map;
    }
  },
  watch: {
    "formData": {
      deep: true,
      handler() {
        this.localFormData = structuredClone(this.formData);
      }
    },
    "localFormData": {
      deep: true,
      handler(newVal) {
        this.$emit('update:formData', newVal);
      }
    },
    "globals.selectedLanguage"() {
      this.globals.getLanguageStrings(this);
    },
    async "selectedLocaleCode"() {
      await this.loadSurveyTranslation();
      this.fillSurveyTranslations();
    }
  },
  created() {
    // this.configureSurveyJs();
  },
  async mounted() {
    if (this.globals.selectedLanguage) { 
      this.globals.getLanguageStrings(this);
    }
    await this.getMasterSurvey(); 
    this.fillContenteditable();
    this.loadSurveyLocales();
  },
  methods: {
    getScenarioName(scenarioId) {
      if(!this.localFormData.scenarios) return;
      let scenario = this.localFormData.scenarios.find(e => e.ScenarioId == scenarioId);
      if(scenario) return scenario.ScenarioName;
    },
    removePlaceholder(eventTarget) {
      if(eventTarget.dataset.placeholder) {
        eventTarget.dataset.placeholder = false;
        eventTarget.textContent = null;
      }
    },
    fillContenteditable() {
      let refs = this.$refs['wizard-survey-contenteditable'];
      if(!refs) return;
      for (const e of refs) {
        const questionGuid = e.dataset.questionguid;
        const choiceGuid = e.dataset.choiceguid;
        const key = e.dataset.key;
        const fallbackText = e.dataset.fallbacktext;
        if(questionGuid) {
          let edited = (this.questionsByGuid[questionGuid] && this.questionsByGuid[questionGuid][key]) ? true : false;
          if(edited) {
            e.textContent = this.questionsByGuid[questionGuid][key];
            e.classList.add('wizard-survey-content-edited');
          } else {
            e.textContent = fallbackText;
          }
        }
        if(choiceGuid) {
          let edited = (this.choicesByGuid[choiceGuid] && this.choicesByGuid[choiceGuid][key]) ? true : false;
          if(edited) {
            e.textContent = this.choicesByGuid[choiceGuid][key];
            e.classList.add('wizard-survey-content-edited');
          } else {
            e.textContent = fallbackText;
          }
        }
      }
    },
    fillSurveyTranslations() {
      let refs = this.$refs['wizard-survey-translation-input'];
      if(!refs) return;
      if(!this.selectedLocaleCode) return;
      const parts = this.selectedLocaleCode.split('-');
      const languageCode = parts[0].toLowerCase();
      const countryCode = parts[1] ? parts[1].toUpperCase() : null;

      for (const e of refs) {
        const guid = e.dataset.translationguid;
        const key = e.dataset.translationkey;
        const category = e.dataset.category;
        const translation = this.translations.find(t => t.Guid.toUpperCase() == guid.toUpperCase() && t.Key == key);
        let translationFromConfiguration;
        let isTranslationFromConfigurationFallback = false;
        switch (category) {

          case 'question':
            if(!this.localFormData.questionsTranslations) break;
            let qt = this.localFormData.questionsTranslations.find(t => 
              t.QuestionGuid.toUpperCase() == guid.toUpperCase() && 
              t.LanguageCode == languageCode && 
              ((!countryCode && !t.CountryCode) || t.CountryCode == countryCode)
            );
            if(!qt) {
              qt = this.localFormData.questionsTranslations.find(t => 
                t.QuestionGuid.toUpperCase() == guid.toUpperCase() && 
                t.LanguageCode == languageCode
              );
              if(qt) isTranslationFromConfigurationFallback = true;
            }
            if(qt) {
              switch (key) {
                case 'title':
                  translationFromConfiguration = qt.TranslationTitle;
                  break;
                case 'description':
                  translationFromConfiguration = qt.TranslationDescription;
                  break;
                default:
                  break;
              }              
            }
            break;

          case 'choice':
            if(!this.localFormData.questionChoicesTranslations) break;
            let qct = this.localFormData.questionChoicesTranslations.find(t => 
              t.ChoiceGuid.toUpperCase() == guid.toUpperCase() && 
              t.LanguageCode == languageCode && 
              ((!countryCode && !t.CountryCode) || t.CountryCode == countryCode)
            );
            if(!qct) {
              qct = this.localFormData.questionChoicesTranslations.find(t => 
                t.ChoiceGuid.toUpperCase() == guid.toUpperCase() && 
                t.LanguageCode == languageCode
              );
              if(qct) isTranslationFromConfigurationFallback = true;
            }
            if(qct) translationFromConfiguration = qct.Translation;
            break;
          default:
            break;
        }
        if(translation) e.value = (translation.Translation ? translation.Translation : (translation.InheritedTranslation ? translation.InheritedTranslation : null));
        if(translationFromConfiguration) {
          e.value = translationFromConfiguration;
          if(!isTranslationFromConfigurationFallback) e.classList.add('wizard-survey-content-edited');
          else e.classList.remove('wizard-survey-content-edited');
        } else {
          e.classList.remove('wizard-survey-content-edited');
        }
      }
    },
    getComment(questionGuid) {
      if(!this.localFormData.questions) return null;
      let e = this.localFormData.questions.find(e => e.QuestionGuid.toUpperCase() == questionGuid.toUpperCase());
      if(e) return e.Comment;
      return null;
    },
    setConfigurationQuestion(questionGuid, key, value, target, type){
      if(!this.localFormData.questions) this.$set(this.localFormData, 'questions', []); 

      if(type === 'number' && !Number.isFinite(Number(value))) {
        this.globals.showErrorSnackbar(`Invalid input. Input must be of type ${type}`);
        return;
      }

      if(value === "") value = null;

      let e = this.localFormData.questions.find(e => e.QuestionGuid.toUpperCase() == questionGuid.toUpperCase());
      if(!e) {
        e = {
          "QuestionGuid": questionGuid,
          "Title": null,
          "Description": null,
          "PointsMax": null,
          "IsSelected": true,
          "Comment": null
        };
        this.$set(e, key, value);
        this.localFormData.questions.push(e);
      } else {
        this.$set(e, key, value);
      }
      target.classList.add('wizard-survey-content-edited');
    },
    setConfigurationQuestionChoice(choiceGuid, key, value, target, type) {
      if(!this.localFormData.questionChoices) this.$set(this.localFormData, 'questionChoices', []);

      if(type === 'number' && !Number.isFinite(Number(value))) {
        this.globals.showErrorSnackbar(`Invalid input. Input must be of type ${type}`);
        return;
      }

      if(value === "") value = null;

      let e = this.localFormData.questionChoices.find(e => e.ChoiceGuid.toUpperCase() == choiceGuid.toUpperCase());
      if(!e) {
        e = {
          "ChoiceGuid": choiceGuid,
          "Title": null,
          "Points": null,
          "PointsMax": null
        };
        this.$set(e, key, value);
        this.localFormData.questionChoices.push(e);
      } else {
        this.$set(e, key, value);
      }
      target.classList.add('wizard-survey-content-edited');
    },
    setConfigurationQuestionTranslation(questionGuid, key, value, target) {
      if(!this.selectedLocaleCode) return;
      const parts = this.selectedLocaleCode.split('-');
      const languageCode = parts[0].toLowerCase();
      const countryCode = parts[1] ? parts[1].toUpperCase() : null;

      if(value === "") value = null;

      if(!this.localFormData.questionsTranslations) this.$set(this.localFormData, 'questionsTranslations', []); 
      let e = this.localFormData.questionsTranslations.find(
        e => e.QuestionGuid.toUpperCase() == questionGuid.toUpperCase() && e.LanguageCode == languageCode && ((!countryCode && !e.CountryCode) || e.CountryCode == countryCode)
      );
      if(!e) {
        e = {
          "QuestionGuid": questionGuid,
          "CountryCode": countryCode,
          "LanguageCode": languageCode,
          "TranslationTitle": null,   // NOT NULL
          "TranslationDescription": null   // NOT NULL
        };
        this.$set(e, key, value);
        this.localFormData.questionsTranslations.push(e);
      } else {
        this.$set(e, key, value);
      }
      target.classList.add('wizard-survey-content-edited');
    },
    setConfigurationQuestionChoiceTranslation(choiceGuid, key, value, target) {
      if(!this.selectedLocaleCode) return;
      const parts = this.selectedLocaleCode.split('-');
      const languageCode = parts[0].toLowerCase();
      const countryCode = parts[1] ? parts[1].toUpperCase() : null;

      if(value === "") value = null;

      if(!this.localFormData.questionChoicesTranslations) this.$set(this.localFormData, 'questionChoicesTranslations', []); 
      let e = this.localFormData.questionChoicesTranslations.find(
        e => e.ChoiceGuid.toUpperCase() == choiceGuid.toUpperCase() && e.LanguageCode == languageCode && ((!countryCode && !e.CountryCode) || e.CountryCode == countryCode)
      );
      if(!e) {
        e = {
          "ChoiceGuid": choiceGuid,
          "CountryCode": countryCode,
          "LanguageCode": languageCode,
          "Translation": null   // NOT NULL
        };
        this.$set(e, key, value);
        this.localFormData.questionChoicesTranslations.push(e);
      } else {
        this.$set(e, key, value);
      }
      target.classList.add('wizard-survey-content-edited');
    },
    async getMasterSurvey() {
      this.masterSurvey = await wizardService.getMasterSurvey(this.localFormData.configurationGuid);
      // this.survey = new SurveyVue.Model(this.masterSurvey.Survey);

      // preselect activeCluster
      if(this.masterSurvey && this.masterSurvey.Survey && this.masterSurvey.Survey.pages) {
        for (const page of this.masterSurvey.Survey.pages) {
          if(page.elements) {
            this.activeCluster = page.elements[0];
            return;
          }
        }
      }

      return;
    },
    validateStep() {
      return { valid: true, msg: null};
    },
    flattenQuestions(elements, pageName, prefix) {
      const out = [];
      (elements || []).forEach((el, idx) => {
        const type = typeof el.getType === "function" ? el.getType() : el.type;
        const title = el.title || el.name || "(untitled)";

        if (type === "panel" || type === "paneldynamic") {
          out.push(...this.flattenQuestions(el.elements || [], pageName, `${prefix}.${el.name || idx}`));
        } else {
          out.push({
            key: `${pageName}:${prefix}:q:${el.name || idx}`,
            title,
            description: el.description,
            type,
            guid: el.guid.toUpperCase(),
            pointsMax: el.pointsMax,
            choices: (el.choices || []).map(c => ({
              value: c.value,
              text: c.text,
              guid: c.guid.toUpperCase(),
              points: c.points,
              maxPoints: c.maxPoints
            })),
            customQuestionNumber: el.customQuestionNumber,
            isRequired: el.isRequired
          });
        }
      });
      return out;
    },
    configureSurveyJs() {
      // Register functions for expression trigger and add maxPointsReached for selectbase questions
      SurveyVue.FunctionFactory.Instance.register("setMaxPoints", (params) => {
        if (params.length < 1 || !this.survey) return;
        for (let i = 0; i < params.length; i++) {
          let q = this.survey.getQuestionByName(params[i]);
          if (q) q.maxPointsReached = true;
        }
      });
      SurveyVue.FunctionFactory.Instance.register(
        "unsetMaxPoints",
        (params) => {
          if (params.length < 1 || !this.survey) return;
          for (let i = 0; i < params.length; i++) {
            let q = this.survey.getQuestionByName(params[i]);
            if (q && q.maxPointsReached) q.maxPointsReached = false;
          }
        }
      );
      SurveyVue.JsonObject.metaData.addProperty("selectbase", {
        name: "maxPointsReached:boolean",
      });
      SurveyVue.JsonObject.metaData.addProperty("panel", {
        name: "isCluster:boolean",
      });
      SurveyVue.JsonObject.metaData.addProperty("panel", {
        name: "backgroundColor:text",
      });
      SurveyVue.JsonObject.metaData.addProperty(
        "question",
        "commentForShopper:text"
      );

      SurveyVue.JsonObject.metaData.addProperty(
        "checkbox",
        "exclusiveValue:checkboxValue"
      );

      // Add points to answers
      SurveyVue.JsonObject.metaData.addClass(
        "itemvaluesWithPoints",
        ["points:number"],
        null,
        "itemvalue"
      );
      SurveyVue.JsonObject.metaData.addProperty(
        "selectbase",
        "choices:itemvalues"
      );
      let property1 = SurveyVue.JsonObject.metaData.findProperty(
        "selectbase",
        "choices"
      );
      property1.typeValue = "itemvaluesWithPoints";
      property1.className = "itemvaluesWithPoints";

      SurveyVue.JsonObject.metaData.addProperty(
        "rating",
        "rateValues:itemvalues"
      );
      property1 = SurveyVue.JsonObject.metaData.findProperty(
        "rating",
        "rateValues"
      );
      property1.typeValue = "itemvaluesWithPoints";
      property1.className = "itemvaluesWithPoints";

      // Add maxPoints to answers of radiogroups
      SurveyVue.JsonObject.metaData.addClass(
        "itemvaluesWithPointsAndMaxPoints",
        ["points:number", "maxPoints:number"],
        null,
        "itemvalue"
      );
      SurveyVue.JsonObject.metaData.addProperty(
        "radiogroup",
        "choices:itemvalues"
      );
      let property2 = SurveyVue.JsonObject.metaData.findProperty(
        "radiogroup",
        "choices"
      );
      property2.typeValue = "itemvaluesWithPointsAndMaxPoints";
      property2.className = "itemvaluesWithPointsAndMaxPoints";

      SurveyVue.JsonObject.metaData.addProperty("question", {
        name: "pointsMax:number",
      });

      SurveyVue.JsonObject.metaData.addProperty("question", "guid");
      SurveyVue.JsonObject.metaData.addProperty("panelbase", "guid");
      SurveyVue.JsonObject.metaData.addProperty("itemvalue", "guid");

      // Add custom question numbers
      SurveyVue.JsonObject.metaData.addProperty("question", {
        name: "customQuestionNumber",
        default: 0,
      });

      // Add properties for currency inputs
      SurveyVue.JsonObject.metaData.addProperty("survey", {
        name: "currencySymbol",
        default: "€",
      });
      SurveyVue.JsonObject.metaData.addProperty("survey", {
        name: "currencyDecimalPoint",
        default: "comma",
      });
    },
    moveHeader(direction) {
      let node = this.$refs["wizard-survey-panel-header-elements"];
      if(!node) return;

      const parent = node.parentElement;
      if (!parent) return;

      const nodeWidth = node.scrollWidth;
      const parentWidth = parent.clientWidth;
      const maxTranslatePx = Math.max(nodeWidth - parentWidth, 0);

      switch (direction) {
        case "l":
          this.translateX -= 50;
          break;
        case "r":
          this.translateX += 50;
          break;
        default:
          break;
      }

      this.translateX = Math.max(0, Math.min(this.translateX, maxTranslatePx));

      node.style.transform = `translateX(-${this.translateX}px)`;
    },
    scrollHeader(event) {
      if(event.deltaY > 0) this.moveHeader('l');
      else this.moveHeader('r');
    },
    loadSurveyLocales() {
      $.ajax({
        method: "GET",
        url: `api/SurveyTranslation/${this.localFormData.surveyId}/Locales`,
        success: (data) => {
          this.surveyLocales = data;
        },
        error: () => {
          console.error(
            "Survey locale data access failed in loadSurveyLocales()"
          );
          this.globals.showErrorSnackbar("Survey locale data access failed");
        },
      });
    },
    loadSurveyTranslation() {
      if(!this.selectedLocaleCode) return;
      return $.ajax({
        method: "GET",
        url: `api/SurveyTranslation/${this.localFormData.surveyId}?localeCode=${this.selectedLocaleCode}`,
        success: (data) => {
          this.translations = data.Translations;
        },
        error: () => {
          console.error(
            "Survey translation data access failed in loadSurveyTranslation()"
          );
          this.globals.showErrorSnackbar("Translation data access failed");
        },
      });
    }
  }
}
</script>

<style lang="css" scoped>
.wizard-survey {
  border: 2px solid #e0e0e0;
}
.wizard-survey-panel-header {
  display: flex;
  flex-direction: row;
  width: 100%;
  cursor: pointer;
}
.wizard-survey-panel-header-btn {
  width: 5%;
  z-index: 1;
  user-select: none;
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 2px solid #e0e0e0;
}
.wizard-survey-panel-header-btn:first-of-type {
  border-right: 1px solid #e0e0e0;
}
.wizard-survey-panel-header-btn:last-of-type {
  border-left: 1px solid #e0e0e0;
}
.wizard-survey-panel-header-elements-container {
  overflow: hidden;
  width: 90%;
}
.wizard-survey-panel-header-elements {
  display: flex;
  flex-direction: row;
  text-wrap-mode: nowrap;
  transition: all .2s;
}
.wizard-survey-panel-header-element {
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 15px;
  border-left: 1px solid #e0e0e0;
  border-bottom: 2px solid #e0e0e0;
  text-align: left;
  font-weight: bold;
  color: gray;
}
.wizard-survey-panel-header-element:first-child {
  border-left: none;
}
.wizard-survey-panel-header-element.active {
  border-top: 1px solid #e0e0e0;
  border-left: 2px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid transparent;
  background: #f8f9fa;
  color: #333333;
}

.wizard-survey-panel-sub-header {
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: space-around;
  background: #f8f9fa;
}
.wizard-survey-panel-locale-header {
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: space-around;
  border-bottom: 1px solid #e0e0e0;
}
.wizard-survey-panel {
  border: 3px solid #e0e0e0;
  margin: 5px;
  transition: border .2s;
}
.wizard-survey-panel:hover {
  border: 3px solid rgb(20, 20, 20);
}
.wizard-survey-panel .wizard-survey-question:last-child {
  /* border-bottom: 0px solid #e0e0e0; */
}
.wizard-survey-panel-title {
  position: relative;
  display: flex;
  justify-content: left;
  align-items: center;
  gap: 5px;
  font-size: 12pt;
  padding-top: 10px;
  padding-bottom: 12px;
  padding-left: 10px;
}

.wizard-survey-locale-code-select {
  width: 50%;
  border: none;
  border-bottom: 1px solid #e0e0e0;
  margin: 10px;
}

.wizard-survey-question {
  border: 1px solid #e0e0e0;
  margin: 5px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: border .2s;
}
.wizard-survey-question:hover {
  border: 1px solid rgb(20, 20, 20);
}
.wizard-survey-row {
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
}
.wizard-survey-row-half {
  width: 50%;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  gap: 5px;
}
.wizard-survey-row textarea {
  width: 100%;
  border: none;
  border-radius: 0%;
  border-bottom: 1px solid #e0e0e0;
}
.span-max-points {
  position: absolute;
  right: 0px;
  top: 0px;
  width: 20%;
}
.wizard-survey-input-isSelected {
  margin: 0px;
  width: 5%;
  cursor: pointer;
}
.wizard-survey-contenteditable {
  border-bottom: 1px solid #e0e0e0;
  min-width: 15px;
  transition: all .2s;
  display: inline-block;
}
.wizard-survey-contenteditable:focus {
  border-bottom: 1px solid black;
}
.placeholder-active {
  color: gray;
}
.wizard-survey-points-container {
  display: flex;
  padding: 0px 10px;
}
.wizard-survey-content-edited {
  border-bottom: 2px solid var(--color-primary) !important;
  color: #333333 !important;
}
.wizard-survey-translation-input {
  border-radius: 0px;
  border: none;
  border-bottom: 1px solid #e0e0e0;
  width: 100%;
  color: gray;
}
.wizard-survey-translation-input:focus {
  border-bottom: 1px solid black;
  color: #333333 !important;
}
.wizard-survey-question-title {
  display: inline; 
  text-align: left; 
  max-width: 75%;
}
.wizard-survey-spacer {
  width: 5%;
}
.scenario-question-settings-container {
  display: flex;
  cursor: default;
}
.scenario-question-settings-container:hover .scenario-question-settings-popup {
  display: block;
}
.scenario-question-settings-popup {
  display: none;
  position: absolute;
  border: 1px solid gray;
  box-shadow: 0px 5px 5px rgb(0 0 0 / 20%);
  padding: 5px;
  transform: translate(10%, 90%);
  z-index: 1;
  background-color: white;
  width: max-content;
}
</style>



// WEBPACK FOOTER //
// Step5.vue?6b12d960